import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import type {FC} from 'react';
import {
  MdDragIndicator,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md';
import {StepSection} from './StepSection';

/**
 * ステップ並び替え用の矢印ボタンコンポーネント（SP版）
 */
const ReorderArrowButton: FC<{
  direction: 'up' | 'down';
  disabled: boolean;
  onClick: () => void;
}> = ({direction, disabled, onClick}) => {
  const Icon = direction === 'up' ? MdKeyboardArrowUp : MdKeyboardArrowDown;
  const label = direction === 'up' ? '1つ上に移動' : '1つ下に移動';
  const baseClassName =
    'p-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const enabledClassName =
    'text-black-2 hover:!text-black-2 hover:!bg-transparent';
  const disabledClassName = 'text-black-3';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${baseClassName} ${disabled ? disabledClassName : enabledClassName}`}
      aria-label={label}
    >
      <Icon size={24} />
    </button>
  );
};

type Props = {
  id: number;
  stepId: number;
  stepName: string;
  result: string | null;
  deadline?: string;
  startDate?: string;
  endDate?: string;
  place?: string;
  notes: string;
  created: string;
  onResultChange?: (stepId: number, result: string) => void;
  onStepFieldUpdate?: (
    stepId: number,
    field:
      | 'stepName'
      | 'deadline'
      | 'startDate'
      | 'endDate'
      | 'place'
      | 'notes',
    value: string | undefined,
  ) => void;
  onGenerateTodos?: (stepId: number) => void;
  isGenerating: boolean;
  shouldShowGeneratePopover: boolean;
  onDeleteStep?: (stepId: number) => void;
  /** SP表示かどうか */
  isSp?: boolean;
  /** SP並び替えモードかどうか */
  isReorderMode?: boolean;
  /** ステップを1つ上に移動 */
  onMoveUp?: (stepId: number) => void;
  /** ステップを1つ下に移動 */
  onMoveDown?: (stepId: number) => void;
  /** 先頭のステップかどうか */
  isFirst?: boolean;
  /** 末尾のステップかどうか */
  isLast?: boolean;
};

export const SortableStepItem: React.FC<Props> = ({
  id,
  stepId,
  stepName,
  deadline,
  result,
  notes,
  startDate,
  endDate,
  place,
  created,
  onResultChange,
  onStepFieldUpdate,
  onGenerateTodos,
  isGenerating,
  shouldShowGeneratePopover,
  onDeleteStep,
  isSp = false,
  isReorderMode = false,
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({id, disabled: isSp});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-stretch gap-8 py-16 px-8 bg-white rounded-16"
    >
      <div className="flex items-center">
        {/* PC: ドラッグハンドル */}
        {!isSp && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-black-2 hover:text-black-1 transition-colors"
            {...attributes}
            {...listeners}
          >
            <MdDragIndicator size={24} />
          </button>
        )}
        {/* SP: 並び替えモード時のみ上下矢印ボタンを表示 */}
        {isSp && isReorderMode && (
          <div className="flex flex-col gap-4">
            <ReorderArrowButton
              direction="up"
              disabled={isFirst}
              onClick={() => onMoveUp?.(stepId)}
            />
            <ReorderArrowButton
              direction="down"
              disabled={isLast}
              onClick={() => onMoveDown?.(stepId)}
            />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <StepSection
          stepId={stepId}
          stepName={stepName}
          deadline={deadline}
          result={result}
          notes={notes}
          startDate={startDate}
          endDate={endDate}
          place={place}
          created={created}
          onResultChange={onResultChange}
          onStepFieldUpdate={onStepFieldUpdate}
          onGenerateTodos={onGenerateTodos}
          isGenerating={isGenerating}
          shouldShowGeneratePopover={shouldShowGeneratePopover}
          onDeleteStep={onDeleteStep}
        />
      </div>
    </div>
  );
};
